/**
 * `ACAD_TABLE` and `OLE2FRAME` group-code readers, plus the ACAD_TABLE body
 * gate. Extracted from `entityParser.ts` (size ceiling) using the same
 * structural-slice seam convention as `entityParser.lineFamily.ts` and
 * `entityParser.hatchBoundary.ts`, so the dependency edge stays
 * one-directional and no require cycle is introduced (ПРИОРИТЕТ #2).
 *
 * Source of truth: Autodesk DXF reference (`ACAD_TABLE`, `OLE2FRAME`) +
 * ezdxf 1.4.4 (`entities/acad_table.py`, `entities/oleframe.py`).
 * AutoCAD MCP is BANNED.
 */

import type { DxfGroup } from "./dxfPrimitives";
import {
  type MutablePoint,
  parseNumberGroup,
  setPointComponent,
} from "./entityParser.groupValueHelpers";

/** The slice of `SupportedEntityDraft` these families read and write. */
type TableOleDraft = {
  points: Record<string, MutablePoint>;
  numbers: Record<string, number | undefined>;
  strings: Record<string, string | undefined>;
  binaryChunks: string[];
  tableContentGroups: DxfGroup[];
  tableRowHeights: number[];
  tableColumnWidths: number[];
  tableBodyStarted: boolean;
  tableContentOpen: boolean;
  tableHeaderCodesSeen: Set<number>;
};

type TableOleGroupReader = (draft: TableOleDraft, value: string) => void;

/** The `100` subclass marker that opens the AcDbTable body. */
export const ACAD_TABLE_BODY_MARKER = "AcDbTable";

/**
 * The AcDbTable HEADER group codes, in the order the Autodesk DXF reference
 * lists them, each of which appears EXACTLY ONCE before the row/column and
 * per-cell records begin.
 *
 * The header ends at 96; everything after it is content. We do not hard-code
 * "the tail starts at 141" because AutoCAD interleaves undocumented flags
 * (a second `280`, a `281`) between 96 and the first 141 — instead the gate
 * below closes the header on the first code that is either not a header code
 * or a REPEAT of one already consumed, which handles both shapes without
 * guessing.
 */
export const ACAD_TABLE_HEADER_CODES: ReadonlySet<number> = new Set([
  280, 342, 343, 11, 21, 31, 90, 91, 92, 93, 94, 95, 96,
]);

/**
 * Feed one group to the ACAD_TABLE body gate.
 *
 * Returns `true` when the group was CONSUMED as content (pushed verbatim to
 * `tableContentGroups`) and the caller must not dispatch it to any reader;
 * `false` when the caller should continue with normal dispatch.
 *
 * The 141 (row height) / 142 (column width) values are ALSO projected into
 * `tableRowHeights` / `tableColumnWidths` WITHOUT being removed from
 * `tableContentGroups` — a read-only projection over a single write source,
 * the same discipline `mtextEmbeddedColumnHeights` uses for MTEXT column
 * heights. Projecting a value for consumers to read must never create a
 * second thing to write.
 */
export function feedAcadTableBodyGate(
  draft: TableOleDraft,
  group: DxfGroup,
): boolean {
  if (group.code === 100 && group.value.trim() === ACAD_TABLE_BODY_MARKER) {
    draft.tableBodyStarted = true;
    return false; // let the common reader record the subclass marker
  }
  if (!draft.tableBodyStarted) return false;
  if (
    !draft.tableContentOpen &&
    ACAD_TABLE_HEADER_CODES.has(group.code) &&
    !draft.tableHeaderCodesSeen.has(group.code)
  ) {
    draft.tableHeaderCodesSeen.add(group.code);
    return false; // header scalar — dispatch to the typed reader
  }
  draft.tableContentOpen = true;
  draft.tableContentGroups.push(group);
  if (group.code === 141) {
    draft.tableRowHeights.push(parseNumberGroup(141, group.value));
  } else if (group.code === 142) {
    draft.tableColumnWidths.push(parseNumberGroup(142, group.value));
  }
  return true;
}

/**
 * `ACAD_TABLE` header readers. Only fire for the codes the gate above lets
 * through, i.e. the first occurrence of each documented header code plus the
 * AcDbBlockReference codes (2, 10/20/30) and the AcDbEntity-level cached
 * graphics blob (160 / 310) that precede the AcDbTable marker.
 */
export const ACAD_TABLE_GROUP_READERS: Record<number, TableOleGroupReader> = {
  // AcDbBlockReference — the anonymous `*T<N>` block holding the rendered
  // table graphics, and its insertion point.
  2: (draft, value) => {
    draft.strings.blockName = value.trim();
  },
  10: (draft, value) => {
    setPointComponent(draft.points, "insertion", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "insertion", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "insertion", "z", 30, value);
  },
  // AcDbEntity-level cached graphics (R2018+): 160 byte count + 310 chunks.
  // Opaque; AutoCAD regenerates it from the `*T<N>` block, which is what we
  // render. Retained verbatim so export reproduces it.
  160: (draft, value) => {
    draft.numbers.cachedGraphicsByteCount = parseNumberGroup(160, value);
  },
  310: (draft, value) => {
    draft.binaryChunks.push(value);
  },
  // AcDbTable header.
  280: (draft, value) => {
    draft.numbers.tableVersion = parseNumberGroup(280, value);
  },
  342: (draft, value) => {
    draft.strings.tableStyleHandle = value.trim();
  },
  343: (draft, value) => {
    draft.strings.blockRecordHandle = value.trim();
  },
  11: (draft, value) => {
    setPointComponent(draft.points, "horizontalDirection", "x", 11, value);
  },
  21: (draft, value) => {
    setPointComponent(draft.points, "horizontalDirection", "y", 21, value);
  },
  31: (draft, value) => {
    setPointComponent(draft.points, "horizontalDirection", "z", 31, value);
  },
  90: (draft, value) => {
    draft.numbers.valueFlag = parseNumberGroup(90, value);
  },
  91: (draft, value) => {
    draft.numbers.rowCount = parseNumberGroup(91, value);
  },
  92: (draft, value) => {
    draft.numbers.columnCount = parseNumberGroup(92, value);
  },
  93: (draft, value) => {
    draft.numbers.overrideFlag = parseNumberGroup(93, value);
  },
  94: (draft, value) => {
    draft.numbers.borderColorOverrideFlag = parseNumberGroup(94, value);
  },
  95: (draft, value) => {
    draft.numbers.borderLineweightOverrideFlag = parseNumberGroup(95, value);
  },
  96: (draft, value) => {
    draft.numbers.borderVisibilityOverrideFlag = parseNumberGroup(96, value);
  },
};

/**
 * `OLE2FRAME` (AcDbOle2Frame) readers.
 *
 * The binary payload (90 byte count + 310 chunks) is an OLE compound
 * document and is NEVER decoded here — see `DxfOle2FrameEntity`'s type doc
 * for why the embedded raster is deliberately not rendered.
 */
export const OLE2FRAME_GROUP_READERS: Record<number, TableOleGroupReader> = {
  70: (draft, value) => {
    draft.numbers.oleVersion = parseNumberGroup(70, value);
  },
  // 3 — the OLE object type string ("Picture (Enhanced Metafile)"). The
  // published group-code table labels this "length of binary data", but every
  // AutoCAD-authored file puts the name here and the byte count in 90. Kept
  // as an opaque string either way.
  3: (draft, value) => {
    draft.strings.objectDescription = value;
  },
  10: (draft, value) => {
    setPointComponent(draft.points, "upperLeft", "x", 10, value);
  },
  20: (draft, value) => {
    setPointComponent(draft.points, "upperLeft", "y", 20, value);
  },
  30: (draft, value) => {
    setPointComponent(draft.points, "upperLeft", "z", 30, value);
  },
  11: (draft, value) => {
    setPointComponent(draft.points, "lowerRight", "x", 11, value);
  },
  21: (draft, value) => {
    setPointComponent(draft.points, "lowerRight", "y", 21, value);
  },
  31: (draft, value) => {
    setPointComponent(draft.points, "lowerRight", "z", 31, value);
  },
  // 71 — OLE object type: 1 link / 2 embedded / 3 static.
  71: (draft, value) => {
    draft.numbers.oleObjectType = parseNumberGroup(71, value);
  },
  // 72 — tile-mode descriptor: 0 model space / 1 paper space.
  72: (draft, value) => {
    draft.numbers.tileModeDescriptor = parseNumberGroup(72, value);
  },
  // 73 — draw aspect (content / thumbnail / icon / doc-print). Present in
  // real files, absent from the published table; parsed and re-emitted
  // verbatim without being interpreted.
  73: (draft, value) => {
    draft.numbers.drawAspect = parseNumberGroup(73, value);
  },
  90: (draft, value) => {
    draft.numbers.binaryByteCount = parseNumberGroup(90, value);
  },
  310: (draft, value) => {
    draft.binaryChunks.push(value);
  },
  // 1 — end-of-OLE-data marker; AutoCAD writes the literal "OLE".
  1: (draft, value) => {
    draft.strings.oleTerminator = value;
  },
};
